import type { ScoringRule } from './scoring';
import type { CardEffectInstance } from './effects';

export const SUITS = [
  'drug',
  'police',
  'money',
  'cars',
  'jewels',
  'casino chips',
  'criminal contacts',
  'weapons'
] as const;

export type Suit = typeof SUITS[number];

// Short slug used to address per-suit assets on disk (card art sheets named
// `gs_<slug>_<rarity>.png`). Two suits get abbreviations because their file
// names dropped the longer form: 'casino chips' → 'chips',
// 'criminal contacts' → 'contacts'.
export const SUIT_SLUG: Record<Suit, string> = {
  'drug': 'drug',
  'police': 'police',
  'money': 'money',
  'cars': 'cars',
  'jewels': 'jewels',
  'casino chips': 'chips',
  'criminal contacts': 'contacts',
  'weapons': 'weapons',
};

// Card rarity tier — drives art sheet variant and (future) loot drop weights.
// Optional on CardData so legacy persisted cards (no rarity column) default to
// 'street' at render time.
export type Rarity = 'street' | 'made' | 'kingpin';

// A concrete mission attached to a Mission card at deck-build time.
// Carries display info plus the scoring rule and the points awarded to
// whichever player wins it at game end.
export interface MissionInstance {
  id: string;
  templateId: string;
  name: string;
  fluff: string;
  rule: ScoringRule;
  winScore: number;
}

export interface CardData {
  id: string;          // unique identifier
  suit: Suit;
  value: number;       // 1-6 for Hand cards, 0 for Mission cards
  type: 'Hand' | 'Mission';
  mission?: MissionInstance;        // populated only when type === 'Mission'
  effects?: CardEffectInstance[];   // ability instances bound at deck-build time
  rarity?: Rarity;                  // visual tier (defaults to 'street' if absent)
  // Set by the `freeze` stateful effect: while > 0 the card cannot be captured.
  // Decremented once per round in stage.onEnd. Absent / 0 = normal card.
  frozenTurns?: number;
  // Human-readable label of the card that applied the freeze (e.g. "4 of
  // police"), shown in the card-detail popup. Cleared when frozenTurns hits 0.
  frozenBy?: string;
}

export interface PlayerState {
  hand: CardData[];
  deck: CardData[];
  discard: CardData[];
  stagingArea: CardData[]; // Newly taken cards awaiting confirmation
  resources: Record<string, number>; // Suited resources extracted
  snatchPoints: number;
  playConfirmed: boolean;
  stageConfirmed: boolean;
  selectedSuits: Suit[];
  // Optional pre-built deck supplied by a registered user picking from their
  // collection. When present, draft.onEnd uses these cards instead of
  // procedurally generating from selectedSuits. Bot players (no account) leave
  // this null and fall back to procedural generation.
  selectedCards?: CardData[] | null;
  readyToPlay: boolean;
  // Index into the avatar spritesheet (0..11). Null = use the initial-fallback.
  // Set from the client during the draft phase via the selectDeck move so the
  // opponent sees it too via playerView.
  avatarId?: number | null;
  // Syndicate del mazzo (coppia di suit con bonus, vedi syndicates.ts), null se nessuno.
  syndicate?: string | null;
}

export interface Dropzone {
  missionCards: CardData[]; // usually 1 per dropzone
  cards: CardData[];        // table cards
  priorityPlayer: string;   // '0' or '1'
  lastTaker: string | null;
}

// A scenografia: the global location active for the whole match.
// Backed by a ScoringRule (same primitives as missions) plus presentation.
export interface LocationData {
  id: string;
  name: string;
  description: string;
  rule: ScoringRule;
  winScore: number;
  background?: string;
}

// Eventi di "snatch" accumulati per tutto il match. Un snatch event = il
// momento in cui un giocatore svuota una dropzone con la sua giocata.
// `capturedCards` include la carta giocata + le carte catturate (cioè TUTTE le
// carte che sono passate dalla dropzone allo stagingArea del taker in quella
// mossa). Il client a fine match filtra per prefisso `p0_*` / `p1_*` per
// distinguere "perse dal player" vs "rubate al player" rispetto al proprio
// seat ID.
export interface MatchSnatchEvent {
  snatcherID: '0' | '1';
  capturedCards: CardData[];
}

export interface MatchSummary {
  snatchEvents: MatchSnatchEvent[];
}

export interface GrandSnatchState {
  players: Record<string, PlayerState>;
  dropzones: [Dropzone, Dropzone];
  roundCount: number;
  // Played cards in the current round, face down
  stagedMoves: Record<string, { card: CardData, dropzoneIndex: number, targetIndex: number } | null>;
  activeLocation: LocationData | null;
  // Diagnostic ledger of snatch events used dal client a fine match per
  // calcolare snatched-from-me / snatched-by-me e POSTare a /auth/runs/.../record-match.
  matchSummary: MatchSummary;
  // Prototipo: 'blocks' = i mazzi arrivano ordinati [tavolo, mano1, mano2]
  // (vedi deckLayout.ts); assente/'random' = distribuzione casuale storica.
  layoutMode?: 'random' | 'blocks';
  // Solo simulazione (setupData.disableSyndicates): niente bonus Syndicate.
  syndicatesDisabled?: boolean;
}
